/**
 * 文件定位：
 * 1. 当前文件位于 cad-core/feature/runtime 层；
 * 2. 负责把拉伸切除专属定义收口为纯数据快照；
 * 3. 该类只处理 cutOptions 的领域复制，不承担 UI、几何或命令装配职责。
 *
 * 文件职责：
 * 1. 显式复制 `ExtrudeCutDefinition` 的白名单字段，避免直接对 Vue Proxy 使用 `structuredClone`；
 * 2. 确保 cutOptions 只保留可序列化的领域数据，防止运行时对象泄漏进定义链；
 * 3. 为 edit / preview / commit / projection 提供统一的 cutOptions 快照入口。
 */
export class ExtrudeCutDefinitionSnapshotBuilder {
    /** 拉伸切除默认距离。 */
    private static readonly DEFAULT_DIRECTION_DISTANCE = 10;

    /**
     * 构建拉伸切除定义快照。
     *
     * 设计说明：
     * 1. Vue reactive / proxy 对象不能直接参与 `structuredClone`，否则浏览器会抛出 `DataCloneError`；
     * 2. 这里使用显式白名单复制，只消费领域模型中允许存在的字段，从底层阻断运行时对象混入；
     * 3. 任何额外挂载字段都会在此被丢弃，因此 `Object3D`、DOM 引用和宿主临时对象无法进入 cad-core 真相源。
     *
     * @param input 原始拉伸切除定义。
     * @returns 纯数据快照；输入为空时返回 `null`。
     */
    public static clone(input: ExtrudeCutDefinition | null | undefined): ExtrudeCutDefinition | null {
        if (!input) {
            return null;
        }

        const direction2Enabled = input.direction2Enabled === true;

        return {
            minimalMode: input.minimalMode !== false,
            flipSideToCut: input.flipSideToCut === true,
            start: this.cloneStart(input.start),
            direction1: this.cloneDirection(input.direction1),
            direction2Enabled,
            direction2: direction2Enabled
                ? this.cloneDirection(input.direction2)
                : undefined,
            featureScope: this.cloneFeatureScope(input.featureScope),
            bodiesToKeep: this.cloneBodiesToKeep(input.bodiesToKeep),
            hostedContourInput: this.cloneHostedContourInput(input.hostedContourInput),
        };
    }

    /**
     * 复制切除起始定义。
     *
     * @param input 原始起始定义。
     * @returns 纯数据起始定义。
     */
    private static cloneStart(input: ExtrudeCutStartDefinition | undefined): ExtrudeCutStartDefinition {
        return {
            type: input?.type ?? 'sketch_plane',
        };
    }

    /**
     * 复制切除方向定义。
     *
     * @param input 原始方向定义。
     * @returns 纯数据方向定义。
     */
    private static cloneDirection(
        input: ExtrudeCutDirectionDefinition | undefined,
    ): ExtrudeCutDirectionDefinition {
        const rawDistance = input?.distance;
        const distance = Number.isFinite(rawDistance)
            ? Math.abs(rawDistance)
            : ExtrudeCutDefinitionSnapshotBuilder.DEFAULT_DIRECTION_DISTANCE;

        return {
            endCondition: input?.endCondition ?? 'blind',
            distance,
            reverseDirection: input?.reverseDirection === true,
        };
    }

    /**
     * 复制 Feature Scope 定义。
     *
     * @param input 原始作用范围定义。
     * @returns 纯数据作用范围定义。
     */
    private static cloneFeatureScope(
        input: ExtrudeFeatureScopeDefinition | undefined,
    ): ExtrudeFeatureScopeDefinition | undefined {
        if (!input) {
            return undefined;
        }

        return {
            mode: input.mode,
            autoSelect: input.autoSelect === true,
            initialAutoTargetIds: this.cloneStringArray(input.initialAutoTargetIds),
            manualTargetIds: this.cloneStringArray(input.manualTargetIds),
        };
    }

    /**
     * 复制 Bodies To Keep 定义。
     *
     * @param input 原始 bodies to keep 定义。
     * @returns 纯数据 bodies to keep 定义。
     */
    private static cloneBodiesToKeep(
        input: ExtrudeCutBodiesToKeepDefinition | undefined,
    ): ExtrudeCutBodiesToKeepDefinition | undefined {
        if (!input) {
            return undefined;
        }

        return {
            enabled: input.enabled === true,
            keptBodyIds: this.cloneStringArray(input.keptBodyIds) ?? [],
        };
    }

    /**
     * 复制宿主轮廓输入定义。
     *
     * @param input 原始宿主轮廓输入。
     * @returns 纯数据宿主轮廓输入。
     */
    private static cloneHostedContourInput(
        input: ExtrudeCutHostedContourInput | undefined,
    ): ExtrudeCutHostedContourInput | undefined {
        if (!input) {
            return undefined;
        }

        return {
            sketchId: input.sketchId,
            contourId: input.contourId,
            targetBodyId: input.targetBodyId ?? null,
            supportFaceObjectId: input.supportFaceObjectId ?? null,
            supportFaceFaceIndex: typeof input.supportFaceFaceIndex === 'number'
                ? input.supportFaceFaceIndex
                : null,
        };
    }

    /**
     * 复制字符串数组。
     *
     * @param input 原始字符串数组。
     * @returns 新数组；输入为空时返回 `undefined`。
     */
    private static cloneStringArray(input: string[] | undefined): string[] | undefined {
        return Array.isArray(input) ? [...input] : undefined;
    }
}
